import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import { Button, Flex, HelperText, Hr, Input, Modal, Typography } from 'components';

import { useProducts } from 'features/products';
import { dateMask, request } from 'app/utils';
import { useSchool } from 'features/school';
import { useAlert } from 'features/alert';
import { AxiosError } from 'axios';

interface JourneyStartDateModalProps {
  refetch: (showLoading?: boolean, showEditLoading?: boolean) => void;
  onClose: () => void;
}

const schema = yup
  .object()
  .shape({
    journeyStartDate: yup.string().required('Você precisa informar uma data de início para a jornada'),
  })
  .required();

const JourneyStartDateModal = ({ refetch, onClose }: JourneyStartDateModalProps) => {
  const { productSlug } = useParams();

  const { productDetails } = useProducts();
  const { apiGatewayUrl } = useSchool();
  const { setAlert, clearAlert, alert } = useAlert();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<{ journeyStartDate: string }>({
    resolver: yupResolver(schema),
    defaultValues: {
      journeyStartDate: '',
    },
  });

  const onSubmit = useCallback(
    async (formData: { journeyStartDate: string }) => {
      const data = formData;

      data.journeyStartDate = moment(`${data.journeyStartDate}`, 'DD/MM/YYYY').format('YYYY-MM-DD');

      request<{ apiResultData: string }>({
        method: 'PATCH',
        url: `/api/v1/School/AssessJourneyStartDate/${productSlug}`,
        baseURL: apiGatewayUrl,
        params: data,
      })
        .then(() => {
          setAlert({ type: 'success', message: 'A data de início da jornada foi alterada com sucesso.' });
          refetch(false, true);

          // setTimeout(() => {
          //   setProductDetails({ ...productDetails, journey: { ...productDetails?.journey, journeyStartDate: data.journeyStartDate } });
          // }, 100);
        })
        .catch((error: AxiosError<{ apiResultMessage: string }>) => {
          let message = 'Ocorreu um erro ao salvar a data de início da jornada.';

          if (error?.response?.data?.apiResultMessage) {
            message = error?.response?.data?.apiResultMessage;
          }

          setAlert({ type: 'error', message });
        });
    },
    [productSlug, productDetails]
  );

  // useEffect(() => {
  //   if (productDetails?.journey) {
  //     reset({
  //       journeyStartDate: productDetails?.journey?.journeyStartDate
  //         ? formatDateFromUtcToTimezone(productDetails?.journey?.journeyStartDate).format('DD/MM/YYYY')
  //         : '',
  //     });
  //   }
  // }, [productDetails?.journey]);

  useEffect(() => {
    return () => {
      clearAlert();
    };
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal onClose={onClose}>
        <Flex flexGrow={0} width={{ d: 540 }} flexDirection="column">
          <Flex flexGrow={0}>
            <Typography type="type4" px={3} py="12px" upper fontWeight="bold">
              Alterar início da jornada
            </Typography>
          </Flex>

          <Hr m={0} />

          <Flex p={3} flexDirection="column" bg="gray_100">
            <Typography type="type1" upper color="gray_500" mb={1}>
              Data de início da jornada
            </Typography>

            <Typography type="type4" color="dark_350" fontWeight="bold" mb={3}>
              {!!productDetails?.journey?.journeyStartDate && moment(productDetails?.journey?.journeyStartDate).format('DD/MM/YYYY')}
            </Typography>

            <Input
              data-error={errors?.journeyStartDate}
              placeholder="Informe a nova data de início da jornada"
              {...register('journeyStartDate')}
              value={dateMask(watch('journeyStartDate'))}
              maxLength={10}
            />
            {!!errors?.journeyStartDate && <HelperText type="error">{errors?.journeyStartDate?.message}</HelperText>}

            {!!alert?.message && (
              <Typography color={alert?.type === 'success' ? 'success_400' : 'alert_400'} fontWeight="bold" mt={3}>
                {alert?.message}
              </Typography>
            )}
          </Flex>

          <Hr m={0} />

          <Button disabled={isSubmitting} type="submit" borderTopLeftRadius={0} borderTopRightRadius={0}>
            Enviar
          </Button>
        </Flex>
      </Modal>
    </form>
  );
};

export default JourneyStartDateModal;
